import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { api } from '../lib/api'

/**
 * Modal de re-aceite legal.
 *
 * Quando admin publica nova versão de Termos/Privacidade/DPA/Signer/RIPD,
 * users que aceitaram a versão antiga (ou nunca aceitaram) recebem este
 * modal bloqueante no próximo carregamento de página autenticada.
 *
 * Lógica: chama GET /api/legal/me/pending no mount. Se devolver array
 * vazio = nada a fazer (no-op). Se devolver itens = modal trava até user
 * checkar todos os "li e aceito" e clicar Confirmar (POST /api/legal/accept).
 *
 * Componente é "best-effort" — em falha de API, não trava o usuário
 * (cliente legítimo não deve sofrer por API com hiccup).
 */

interface PendingDoc {
  id: string
  type: 'TERMS' | 'PRIVACY' | 'DPA' | 'SIGNER_TERMS' | 'RIPD'
  version: string
  title: string
  publishedAt: string
}

const TYPE_PATH: Record<string, string> = {
  TERMS:        '/termos',
  PRIVACY:      '/privacidade',
  DPA:          '/dpa',
  SIGNER_TERMS: '/termos-signatario',
  RIPD:         '/termos', // RIPD não tem página pública — link genérico
}

export default function LegalAcceptanceModal() {
  const [pending, setPending] = useState<PendingDoc[]>([])
  const [accepted, setAccepted] = useState<Set<string>>(new Set())
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    const token = localStorage.getItem('pakt_token')
    if (!token) return
    api.get<{ pending: PendingDoc[] }>('/api/legal/me/pending')
      .then(r => {
        // RIPD não é doc de aceite obrigatório — filtra fora se aparecer
        setPending(r.pending.filter(d => d.type !== 'RIPD'))
      })
      .catch(() => null) // silencioso — não trava em falha de rede
  }, [])

  if (pending.length === 0) return null

  function toggle(id: string) {
    setAccepted(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const allChecked = pending.every(p => accepted.has(p.id))

  async function submit() {
    if (!allChecked) return
    setSubmitting(true)
    setError('')
    try {
      await api.post('/api/legal/accept', { documentIds: pending.map(p => p.id) })
      setPending([]) // some o modal
    } catch (err: any) {
      setError(err?.body?.message || err?.message || 'Falha ao registrar aceite')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4">
      <div className="bg-white rounded-xl max-w-lg w-full max-h-[90vh] flex flex-col">
        <header className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-bold text-gray-900">
            {pending.length === 1 ? 'Atualização nos nossos termos' : 'Atualizamos nossos documentos legais'}
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            Pra continuar usando a PAKT, revise e aceite a{pending.length === 1 ? '' : 's'} nova{pending.length === 1 ? '' : 's'} versão{pending.length === 1 ? '' : 'ões'} abaixo.
          </p>
        </header>

        <div className="px-6 py-4 overflow-y-auto flex-1 space-y-3">
          {pending.map(doc => (
            <label key={doc.id} className="flex items-start gap-3 cursor-pointer p-3 border border-gray-200 hover:border-indigo-300 rounded-lg">
              <input
                type="checkbox"
                checked={accepted.has(doc.id)}
                onChange={() => toggle(doc.id)}
                className="mt-1"
              />
              <div className="flex-1">
                <div className="font-medium text-gray-900">{doc.title}</div>
                <div className="text-xs text-gray-500 font-mono mt-0.5">
                  versão {doc.version} · publicada {new Date(doc.publishedAt).toLocaleDateString('pt-BR')}
                </div>
                <Link
                  to={TYPE_PATH[doc.type] ?? '/'}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-indigo-600 hover:underline mt-1 inline-block"
                  onClick={(e) => e.stopPropagation()}
                >
                  Ler {doc.title.toLowerCase()} ↗
                </Link>
              </div>
            </label>
          ))}

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded text-sm">{error}</div>
          )}
        </div>

        <footer className="px-6 py-4 border-t border-gray-200">
          <button
            onClick={submit}
            disabled={!allChecked || submitting}
            className="w-full bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2.5 rounded-lg font-semibold disabled:opacity-50"
          >
            {submitting ? 'Registrando...' : 'Li e aceito todos'}
          </button>
          <p className="text-xs text-gray-500 mt-2 text-center">
            Você precisa marcar e aceitar cada documento pra continuar.
            <br/>
            Discordou? Faça <Link to="/privacidade/exercer-direitos" className="underline">exclusão de conta</Link>.
          </p>
        </footer>
      </div>
    </div>
  )
}
